// src/services/chatService.ts
import { logger } from '../utils/logger';

import { supabase } from './supabase';
import { moderateChatMessage } from "./contentModeration";
import { clearUnreadCache } from './unreadService';

export interface Message {
  id: string;
  match_id: string;
  sender_id: string;
  content: string;
  image_url?: string;
  read: boolean;
  created_at: string;
}

// Debounce map for mark-as-read operations
const markAsReadDebounce = new Map<string, NodeJS.Timeout>();

/**
 * Send a message to a match
 */
export async function sendMessage(
  matchId: string,
  content: string,
  imageUrl?: string
): Promise<{ success: boolean; message?: Message; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Content moderation - block hate speech and slurs
    const moderationCheck = moderateChatMessage(content);
    if (!moderationCheck.isClean) {
      return {
        success: false,
        error: 'Message contains inappropriate content. This platform does not tolerate hate speech or slurs.'
      };
    }

    // 1. Save message to database
    const { data: message, error: messageError } = await supabase
      .from('messages')
      .insert({
        match_id: matchId,
        sender_id: user.id,
        content: content,
        image_url: imageUrl || null,
        read: false
      })
      .select()
      .single();

    if (messageError) {
      logger.error('Error sending message:', messageError);
      return { success: false, error: messageError.message };
    }

    // 2. Get recipient ID from match
    const { data: match, error: matchError } = await supabase
      .from('matches')
      .select('user1_id, user2_id')
      .eq('id', matchId)
      .single();

    if (matchError) {
      logger.error('Error fetching match:', matchError);
      // Message was sent, but notification will fail - that's okay
      return { success: true, message };
    }

    const recipientId = match.user1_id === user.id ? match.user2_id : match.user1_id;

    // 3. Send push notification (don't await - fire and forget)
    supabase.functions.invoke('send-notification', {
      body: {
        type: 'new_message',
        recipientId: recipientId,
        senderId: user.id,
        matchId: matchId,
        messagePreview: content.substring(0, 50)
      }
    }).then(result => {
      if (result.error) {
        logger.error('Push notification error:', result.error);
      }
    });

    return { success: true, message };
  } catch (error) {
    logger.error('Error in sendMessage:', error);
    return { success: false, error: 'Failed to send message' };
  }
}

const PAGE_SIZE = 50;

/**
 * Get messages for a match with cursor-based pagination.
 * Pass `before` (a created_at ISO string) to load messages older than that cursor.
 * Returns `hasMore: true` when there are additional pages available.
 */
export async function getMessages(
  matchId: string,
  before?: string
): Promise<{ success: boolean; messages?: Message[]; hasMore?: boolean; error?: string }> {
  try {
    // Fetch one extra record to determine whether an older page exists
    let query = supabase
      .from('messages')
      .select('*')
      .eq('match_id', matchId)
      .order('created_at', { ascending: false })
      .limit(PAGE_SIZE + 1);

    if (before) {
      query = query.lt('created_at', before);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching messages:', error);
      return { success: false, error: error.message };
    }

    const hasMore = (data || []).length > PAGE_SIZE;
    const page = hasMore ? data!.slice(0, PAGE_SIZE) : (data || []);

    // Reverse to show chronologically (oldest first)
    return { success: true, messages: page.reverse(), hasMore };
  } catch (error) {
    logger.error('Error in getMessages:', error);
    return { success: false, error: 'Failed to fetch messages' };
  }
}

/**
 * Mark all messages in a match as read (with debouncing to prevent excessive DB calls)
 */
export async function markAllMessagesAsRead(
  matchId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    // Debounce mark-as-read operations per match
    const debounceKey = `${user.id}:${matchId}`;
    const existingTimer = markAsReadDebounce.get(debounceKey);

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    return new Promise((resolve) => {
      const timer = setTimeout(async () => {
        markAsReadDebounce.delete(debounceKey);

        // Mark all messages as read where current user is NOT the sender
        const { error } = await supabase
          .from('messages')
          .update({ read: true })
          .eq('match_id', matchId)
          .neq('sender_id', user.id)
          .eq('read', false);

        if (error) {
          logger.error('Error marking messages as read:', error);
          resolve({ success: false, error: error.message });
          return;
        }

        // Clear unread cache to force refresh of counts
        clearUnreadCache();

        resolve({ success: true });
      }, 500); // 500ms debounce

      markAsReadDebounce.set(debounceKey, timer);
    });

  } catch (error) {
    logger.error('Error in markAllMessagesAsRead:', error);
    return { success: false, error: 'Failed to mark messages as read' };
  }
}

/**
 * Get unread message count for a match
 */
export async function getUnreadCount(
  matchId: string
): Promise<{ success: boolean; count?: number; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const { data, error, count } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('match_id', matchId)
      .neq('sender_id', user.id)
      .eq('read', false);

    if (error) {
      logger.error('Error getting unread count:', error);
      return { success: false, error: error.message };
    }

    return { success: true, count: count || 0 };
  } catch (error) {
    logger.error('Error in getUnreadCount:', error);
    return { success: false, error: 'Failed to get unread count' };
  }
}

/**
 * Subscribe to new messages in a match
 */
export function subscribeToMessages(matchId: string, callback: (message: Message) => void) {
  const subscription = supabase
    .channel(`messages:${matchId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `match_id=eq.${matchId}`
      },
      (payload) => {
        callback(payload.new as Message);
      }
    )
    .subscribe();

  return subscription;
}

/**
 * Unsubscribe from message updates
 */
export async function unsubscribeFromMessages(subscription: any) {
  await supabase.removeChannel(subscription);
}

/**
 * Clear all pending debounced operations (useful for cleanup)
 */
export function clearPendingMarkAsRead() {
  markAsReadDebounce.forEach(timer => clearTimeout(timer));
  markAsReadDebounce.clear();
}